/* eslint-disable react/prop-types */
import React, { Component } from "react";
import { RouteComponentProps } from "react-router-dom";
import { useTranslation, WithTranslation } from "react-i18next";
import {
    Container,
    Theme,
    Typography,
    withStyles,
    WithStyles,
    createStyles,
    List,
    ListItem,
    Fade,
} from "@material-ui/core/";

import { fetchImageWithAccessToken } from "src/utils";
import { apiUrl } from "src/settings";
import { Site, SiteDetails } from "src/entities";

import { useVideoStreams } from "src/hooks/useVideoStreams";
import { useConfigurations } from "src/hooks/useConfigurations";
import { SiteOverviewVideoStream } from "./SiteOverviewVideoStream";
import { SiteOverviewConfigurationGroup } from "./SiteOverviewConfigurationGroup";
import "./_configuration-groups.scss";
import "./_site-overview.scss";
import "./_video-stream-list.scss";

const themedStyle = (theme: Theme) =>
    createStyles({
        siteOverviewListPrimary: {
            color: theme.palette.text.secondary,
        },
        siteOverviewListSecondary: {
            color: theme.palette.text.primary,
        },
        siteOverviewListSpacer: {
            background: theme.palette.divider,
        },
    });

type HookProps = {
    videoStreamsIsLoading: boolean;
    configurationsIsLoading: boolean;
    t: any;
};

type OwnProps = {
    site: Site;
    siteDetails: SiteDetails;
    siteDetailsIsLoading: boolean;
    theme: Theme;
    hookProps: HookProps;
};

type Props = OwnProps & WithStyles<typeof themedStyle> & RouteComponentProps & WithTranslation & HookProps;

class SiteOverviewInternal extends Component<Props, any> {
    constructor(props) {
        super(props);

        this.state = {
            imageUrl: "",
        };
    }
    componentDidMount() {
        const sub = fetchImageWithAccessToken(`${apiUrl}/sites/${this.props.site.id}/map/image.jpg`).subscribe(
            (response) => {
                sub.unsubscribe();
                this.setState((state) => ({
                    ...state,
                    imageUrl: response.url,
                }));
            },
        );
    }

    render() {
        const { classes } = this.props;
        return (
            <div className="site-overview">
                {this.props.site ? (
                    <React.Fragment>
                        <div className="sites-card">
                            <Fade in={true} timeout={{ enter: 150 }}>
                                <div className="site-overview__tab">
                                    <Container>
                                        <img
                                            src={this.state.imageUrl}
                                            alt={this.props.site.name}
                                            className="site-overview__map"
                                        />
                                    </Container>
                                    <List dense={true} disablePadding={true} className="site-overview__list">
                                        <ListItem disableGutters={true}>
                                            <Typography variant="subtitle1">{this.props.site.name}</Typography>
                                        </ListItem>
                                        <ListItem disableGutters={true}>
                                            <div
                                                className={`site-overview__list-item site-overview__list-item--primary ${classes.siteOverviewListPrimary}`}
                                            >
                                                {this.props.hookProps.t("Entities.TimeZone.TimeZone")}
                                            </div>
                                            <div
                                                className={`site-overview__list-item site-overview__list-item--spacer ${classes.siteOverviewListSpacer}`}
                                            ></div>
                                            <div
                                                className={`site-overview__list-item site-overview__list-item--secondary ${classes.siteOverviewListSecondary}`}
                                            >
                                                {this.props.site.timeZone}
                                            </div>
                                        </ListItem>
                                    </List>
                                </div>
                            </Fade>
                        </div>
                        {!this.props.siteDetailsIsLoading &&
                        !this.props.hookProps.videoStreamsIsLoading &&
                        !this.props.hookProps.configurationsIsLoading &&
                        this.props.siteDetails ? (
                            <React.Fragment>
                                <h3 className="title title--h3">
                                    {this.props.hookProps.t("Entities.AlarmHandling.CamerasOnSite")}
                                </h3>
                                <div className="video-stream-list">
                                    {Object.values(this.props.siteDetails.videoStreams).map((videoStream: any) => {
                                        const { functionalities } = videoStream;
                                        if (videoStream) {
                                            return (
                                                <SiteOverviewVideoStream
                                                    key={`${videoStream.id}`}
                                                    videoStream={videoStream}
                                                    functionalities={functionalities}
                                                    timeZone={this.props.site.timeZone}
                                                />
                                            );
                                        }
                                        return null;
                                    })}
                                </div>

                                <h3 className="title title--h3">
                                    {this.props.hookProps.t("Entities.ConfigurationGroup.ConfigurationGroups")}
                                </h3>
                                <div className="configuration-groups">
                                    {Object.values(this.props.siteDetails.configurationGroups)?.map(
                                        (configurationGroup: any) => {
                                            if (configurationGroup) {
                                                return (
                                                    <SiteOverviewConfigurationGroup
                                                        key={`${configurationGroup.id}`}
                                                        configurationGroup={configurationGroup}
                                                    />
                                                );
                                            }
                                            return null;
                                        },
                                    )}
                                </div>
                            </React.Fragment>
                        ) : null}
                    </React.Fragment>
                ) : null}
            </div>
        );
    }
}

// Functional wrapper
const SiteOverviewWrapper: React.FC<OwnProps & RouteComponentProps & WithTranslation & HookProps> = (props) => {
    const { t } = useTranslation();
    const { videoStreams, videoStreamsIsLoading } = useVideoStreams(props.site.id);
    const { configurations, configurationsIsLoading } = useConfigurations(props.site.id);

    props.siteDetails.videoStreams = videoStreams || [];
    props.siteDetails.configurationGroups = configurations || [];
    props.hookProps.videoStreamsIsLoading = videoStreamsIsLoading;
    props.hookProps.configurationsIsLoading = configurationsIsLoading;
    props.hookProps.t = t;

    return (
        <SiteOverviewInternal
            classes={{
                siteOverviewListPrimary: "",
                siteOverviewListSecondary: "",
                siteOverviewListSpacer: "",
            }}
            {...props}
        />
    );
};

export const SiteOverview = withStyles(themedStyle)(SiteOverviewWrapper);
